//Imports
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
  Container,
  TableView,
  SearchInput,
  TableButtons,
  IconButton,
  MenuComponent,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";
import { HasPermission } from "Core/security";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "Components/Notifications";
import Preferences from "Core/containers/Preferences";

//Actions
import { reset, manageSearchEquipments } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalDeleteManageEquipment } from "./modals/deleteManageEquipment/redux/slice";
import DeleteManageEquipment from "./modals/deleteManageEquipment";
import { openModalCreateEditManageEquipment } from "./modals/createEditManageEquipment/redux/slice";
import CreateEditManageEquipment from "./modals/createEditManageEquipment";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";

const KEY_PREFERENCE = "Manage-EquipmentTab-list";
const INITIAL_SORT = { key: "name", order: "asc" };

const EquipmentTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferences, setPreferences] = useState({})
  const [loadPreferences, setLoadPreferences] = useState(true)
  const [loadColumns, setLoadColumns] = useState(false)

  //Language
  const { messages, locale } = useIntl();

  //Table refs
  const equipmentTableRef = useRef();

  //Redux
  const dispatch = useDispatch();
  const pagination = useTablePagination({
    dispatch,
    action: manageSearchEquipments,
    searchFields: ["name"],
    tableRef: equipmentTableRef,
    sendGenericExport,
    exportName: "equipments"
  });
  const { loading, equipments, total, error } = useSelector((state) => state.manageEquipmentTabRedux);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const openDeleteManageEquipment = useSelector(
    (state) => state.manageDeleteManageEquipmentRedux.open
  );
  const openCreateEditManageEquipment = useSelector(
    (state) => state.manageCreateEditManageEquipmentRedux.open
  );
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const user = useSelector((state) => state.securityRedux.user);

  // Permissions
  const editablePermissions = HasPermission(user, "", [336])


  if (error) {
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }

  if (exportResult && exportName === "equipments") {
    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info", 
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }

  //Options
  const menuEquipmentTabOptions = [messages["edit"], messages["delete"]];

  //Memos 
  const currentColumns = useMemo(
    () => [
      {
        key: "name",
        title: messages["manage_equipmentsTable_equipmentName"],
        functionString: 'return row?.name || ""',
        show: true,
        export: true,
        sort: true,
        exportValue: (row) => row?.name,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row.name}</p>
            </Grid>
            {editablePermissions &&
              <Grid item>
                <MenuComponent
                  placement="right-start"
                  disabled={loading}
                  options={menuEquipmentTabOptions}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickMenu}
                  value={row}
                />
              </Grid>
            }
          </Grid>
        )
      },
      {
        key: "assignments",
        title: messages["manage_equipmentsTable_assignments"],
        functionString: 'return row?.unitsCount || ""',
        show: true,
        export: true,
        exportValue: (row) => row?.unitsCount || "",
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              <p>{row?.unitsCount || ""}</p>
            </Grid>
          </Grid>
        )
      }
    ],
    [locale]
  );

  //Handlers and utils
  const handleClickMenu = (type, equipment) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageEquipment(equipment));
      return;
    }
    if (type === messages["delete"]) {
      if (equipment.id) dispatch(openModalDeleteManageEquipment({ deleteEquipment: equipment }));
      return;
    }
  };

  const handleDeleteEquipmentList = useCallback(() => {
    dispatch(openModalDeleteManageEquipment({ deleteEquipmentList: selectedItems }));
  }, [selectedItems]);

  const savePreferences = useCallback((preferencesArray) => {
    if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
    }

    let updatedPreferences = { ...preferences } || {};

    preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
    });

    setPreferences(updatedPreferences);

    dispatch(savePreference({
        entityName: "User",
        entityId: authenticatedId,
        preferenceKey: KEY_PREFERENCE,
        preferenceValue: updatedPreferences
    }));
}, [preferences]);

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      pagination.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (pagination.columns.length > 0) {
        setPreferences({ ...preferences, columns: pagination.columns })
        setLoadColumns(false);
    }
  }, [locale]);

  useEffect(() => {
    pagination.setColumns(currentColumns);
  }, [currentColumns]);

  return (
    <Fragment>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["add"]}
                    placement="bottom"
                    variant="secondary"
                    icon="add_box"
                    size="small"
                    disabled={loading}
                    onClick={() => dispatch(openModalCreateEditManageEquipment())}
                  />
                }
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["delete"]}
                    placement="bottom"
                    variant="secondary"
                    icon="delete"
                    size="small"
                    onClick={() => handleDeleteEquipmentList()}
                    style={{ opacity: !selectedItems.length && "0.6" }}
                    isDisabled={!selectedItems.length}
                  />
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...pagination}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    pagination.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_equipmentsTable_search_by_equipment_name"]}
              searchText={pagination.searchText}
              onSearchText={pagination.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              equipmentTableRef.current?.setSort?.(sort);
              pagination.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loading || loadPreferences}
            topheight={235}
            data={equipments}
            total={total}
            disableItemSelection={disableItemSelection(pagination)}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...pagination}
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              pagination.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openDeleteManageEquipment && (
        <DeleteManageEquipment refresh={() => {
          pagination.onSearchChange("");
        }} />
      )}
      {openCreateEditManageEquipment && (
        <CreateEditManageEquipment refresh={equipmentTableRef?.current?.refreshTable} />
      )}
    </Fragment>
  );
};

export default EquipmentTab;
